// Gallery.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// === IMAGES ===
import galleryHeroImage from '../assets/pick/3.jpg';

// Gallery 1
import nuwaraEliya from '../assets/pick2/Nuware Eliya/istockphoto-579253918-612x612.jpg';
import sigiriya1 from '../assets/pick2/sigiriya/istockphoto-1492229422-612x612.jpg';
import anuradhapura from '../assets/pick2/Anuradapura/istockphoto-1178654295-612x612.jpg';
import rambodaFalls from '../assets/pick/4.jpg';
import ella1 from '../assets/pick2/Ella/pexels-iakub-arifulin-205356454-11675420.jpg';
import yala from '../assets/pick2/yala/Sri-Lanka-Yala-National-Park-olifanten-safari.webp';
import kandy from '../assets/pick2/kandy/pexels-freestockpro-322437.jpg';
import bodhiViharaya from '../assets/pick/Sri_Maha_Bodhi_Vihara_Statue.jpg';

// Gallery 2
import knuckles from '../assets/pick/istockphoto-2177599886-612x612.jpg';
import udawalawe from '../assets/pick2/udawalawe/istockphoto-904632224-612x612.jpg';

// Gallery 3
import bentota from '../assets/pick2/benthota/Bentota_River_boat_safari_Feachered_1a66874bcb.jpg';
import colombo from '../assets/pick2/colombo/pexels-amayuru-2370934.jpg';
import galle from '../assets/pick2/Galle/istockphoto-472387838-612x612.jpg';
import mirrissa from '../assets/pick2/Mirissa/pexels-ollivves-1005417.jpg';
import jaffna from '../assets/pick/5.jpg';
import polonnaruwa from '../assets/pick2/polonnaruwa/istockphoto-1184393376-612x612.jpg';
import turtle from '../assets/pick2/turtle/istockphoto-1170317746-612x612.jpg';
import ambuluwawaTower from '../assets/pick2/Ambuluwawa/b8b15f8ac9dea867fa967e7fd0c36b6793d914541adf48cf31358408e01c93a1.avif';

// Gallery 4
import polgampala from '../assets/pick/44.jpg';
import beach from '../assets/pick/43.jpg';

// === DATA (Tradotto con t()) ===
const Gallery = () => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(-1);
    const location = useLocation();

    const gallery1 = [
        { image: nuwaraEliya, label: t('gallery.labels.nuwaraEliya'), span: "col-span-1 row-span-1" },
        { image: sigiriya1, label: t('gallery.labels.sigiriya'), span: "col-span-1 row-span-2" },
        { image: anuradhapura, label: t('gallery.labels.anuradhapura'), span: "col-span-1 row-span-1" },
        { image: rambodaFalls, label: t('gallery.labels.rambodaFalls'), span: "col-span-1 row-span-2" },
        { image: ella1, label: t('gallery.labels.ella'), span: "col-span-1 row-span-2" },
        { image: yala, label: t('gallery.labels.yala'), span: "col-span-1 row-span-2" },
        { image: kandy, label: t('gallery.labels.kandy'), span: "col-span-1 row-span-1" },
        { image: bodhiViharaya, label: t('gallery.labels.bodhiViharaya'), span: "col-span-1 row-span-1" },
    ];

    const gallery2 = [
        { image: knuckles, label: t('gallery.labels.knuckles') },
        { image: udawalawe, label: t('gallery.labels.udawalawe') },
    ];

    const gallery3 = [
        { image: bentota, label: t('gallery.labels.bentota'), span: "col-span-1 row-span-1" },
        { image: colombo, label: t('gallery.labels.colombo'), span: "col-span-1 row-span-2" },
        { image: galle, label: t('gallery.labels.galle'), span: "col-span-1 row-span-1" },
        { image: mirrissa, label: t('gallery.labels.mirrissa'), span: "col-span-1 row-span-2" },
        { image: jaffna, label: t('gallery.labels.jaffna'), span: "col-span-1 row-span-2" },
        { image: ambuluwawaTower, label: t('gallery.labels.ambuluwawa'), span: "col-span-1 row-span-2" },
        { image: turtle, label: t('gallery.labels.turtle'), span: "col-span-1 row-span-1" },
        { image: polonnaruwa, label: t('gallery.labels.polonnaruwa'), span: "col-span-1 row-span-1" },
    ];

    const gallery4 = [
        { image: polgampala, label: t('gallery.labels.polgampala') },
        { image: beach, label: t('gallery.labels.beach') },
    ];

    // Combine all for Lightbox
    const allImages = [...gallery1, ...gallery2, ...gallery3, ...gallery4];
    const slides = allImages.map(item => ({
        src: item.image,
        width: 1600,
        height: 1067,
        alt: item.label,
        title: item.label,
    }));

    // Offsets for correct indexing
    const g1 = gallery1.length;
    const g2 = g1 + gallery2.length;
    const g3 = g2 + gallery3.length;

    return (
        <div className="bg-black min-h-screen">
            {/* ===== HERO SECTION ===== */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={{
                    hidden: { opacity: 0, y: 50 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
                }}
                className="relative h-[700px] w-full bg-cover bg-center md:h-screen md:max-h-[740px] lg:max-h-[800px] xl:max-h-[960px]"
                style={{ backgroundImage: `url(${galleryHeroImage})` }}
            >
                <div className="absolute inset-0 bg-black/40" />
                <div className="container relative mx-auto px-4 h-full flex items-end justify-end pb-20 md:pb-32 lg:pb-40">
                    <div className="text-right">
                        <h1
                            className="font-konkhmer text-[3.125rem] uppercase md:text-5xl lg:text-[5rem] font-bold"
                            style={{
                                background: "linear-gradient(to right, #009CE6, #96BA33)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                            }}
                        >
                            {t('gallery.pageTitle')}
                        </h1>
                        <div className="mt-4 max-w-[245px] md:max-w-[300px] lg:max-w-[670px]">
                            <p className="font-inknut text-xs leading-6 text-white md:text-sm lg:text-lg lg:leading-8">
                                {t('gallery.subtitle')}
                            </p>
                            <div
                                className="mt-3 h-[5px] w-[100px] md:w-[120px]"
                                style={{
                                    background: "linear-gradient(to right, #009CE6, #96BA33)",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* ===== GALLERY CONTENT SECTION ===== */}

            <div className="container mx-auto px-4 py-10">
                {/* Tabs */}
                <div className="flex items-center justify-center gap-4 font-redHatDisplay text-2xl font-bold uppercase md:text-3xl lg:text-4xl">
                    <Link
                        to="/gallery"
                        className={`pb-2 border-b-4 transition-all ${location.pathname === "/gallery"
                            ? "border-[#009CE6] text-[#009CE6]"
                            : "border-transparent text-white hover:text-[#96BA33]"
                            }`}
                    >
                        {t('gallery.tabs.places')}
                    </Link>
                    <span className="text-white/60">/</span>
                    <Link
                        to="/events"
                        className={`pb-2 border-b-4 transition-all ${location.pathname === "/events"
                            ? "border-[#96BA33] text-[#96BA33]"
                            : "border-transparent text-white hover:text-[#009CE6]"
                            }`}
                    >
                        {t('gallery.tabs.events')}
                    </Link>
                </div>

                {/* GALLERY 1 */}
                <div className="mt-12 grid auto-rows-[180px] grid-cols-2 gap-3 md:auto-rows-[250px] md:grid-cols-3 md:gap-4 lg:auto-rows-[420px] lg:gap-6">
                    {gallery1.map((item, i) => (
                        <motion.button
                            key={i}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => setIndex(i)}
                            className={`group relative overflow-hidden rounded-xl shadow-lg ${item.span}`}
                        >
                            <img
                                src={item.image}
                                alt={item.label}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-grad-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-0 p-4">
                                    <h3 className="font-konkhmer text-sm font-bold uppercase text-white md:text-lg lg:text-xl">
                                        {item.label}
                                    </h3>
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>

                {/* GALLERY 2 */}
                <div className="mt-8 flex flex-col gap-3 md:mt-12 md:flex-row md:gap-4 lg:gap-6">
                    {gallery2.map((item, i) => (
                        <motion.button
                            key={i}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => setIndex(g1 + i)}
                            className="group relative h-[220px] w-full overflow-hidden rounded-xl shadow-lg md:h-[420px] md:w-1/2"
                        >
                            <img
                                src={item.image}
                                alt={item.label}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-0 p-4">
                                    <h3 className="font-konkhmer text-sm font-bold uppercase text-white md:text-lg lg:text-xl">
                                        {item.label}
                                    </h3>
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>

                {/* GALLERY 3 */}
                <div className="mt-6 grid auto-rows-[180px] grid-cols-2 gap-3 md:auto-rows-[250px] md:grid-cols-3 md:gap-4 lg:auto-rows-[420px] lg:gap-6">
                    {gallery3.map((item, i) => (
                        <motion.button
                            key={i}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => setIndex(g2 + i)}
                            className={`group relative overflow-hidden rounded-xl shadow-lg ${item.span}`}
                        >
                            <img
                                src={item.image}
                                alt={item.label}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-0 p-4">
                                    <h3 className="font-konkhmer text-sm font-bold uppercase text-white md:text-lg lg:text-xl">
                                        {item.label}
                                    </h3>
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>

                {/* GALLERY 4 */}
                <div className="mt-8 flex flex-col gap-3 md:mt-12 md:flex-row md:gap-4 lg:gap-6 pb-10">
                    {gallery4.map((item, i) => (
                        <motion.button
                            key={i}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() => setIndex(g3 + i)}
                            className="group relative h-[220px] w-full overflow-hidden rounded-xl shadow-lg md:h-[420px] md:w-1/2"
                        >
                            <img
                                src={item.image}
                                alt={item.label}
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-0 p-4">
                                    <h3 className="font-konkhmer text-sm font-bold uppercase text-white md:text-lg lg:text-xl">
                                        {item.label}
                                    </h3>
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>

            {/* LIGHTBOX */}
            <Lightbox
                open={index >= 0}
                close={() => setIndex(-1)}
                index={index}
                slides={slides}
                plugins={[Thumbnails, Zoom]}
                thumbnails={{
                    position: "bottom",
                    width: 100,
                    height: 70,
                    border: 2,
                    borderColor: "#009CE6",
                    borderRadius: 8,
                    padding: 4,
                    gap: 12,
                }}
                zoom={{ maxZoomPixelRatio: 3 }}
                styles={{
                    container: { backgroundColor: "rgba(0,0,0,0.95)" },
                    thumbnail: { cursor: "pointer" },
                }}
                controller={{ closeOnBackdropClick: true }}
            />

        </div>
    );
};

export default Gallery;